import { zodResolver } from "@hookform/resolvers/zod";
import ProductionValidator from "Validators/Admin/Production";
import { EditorState } from "draft-js";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import Braile from "Assets/Common/Accessiblity/Braile.png";
import Cadeirante from "Assets/Common/Accessiblity/Cadeirante.png";
import Caption from "Assets/Common/Accessiblity/Caption.png";
import Libras from "Assets/Common/Accessiblity/Libras.png";
import Surdo from "Assets/Common/Accessiblity/Surdo.png";
import Text from "Assets/Common/Accessiblity/Text.png";

import { HTTPErrorCallback } from "Helpers/Error";
import { imageExtensions } from "Helpers/File";
import { transformEditorStateIntoData } from "Helpers/Util";

import UseFile from "Hooks/UseFile";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import DateValidity from "Components/Admin/DateValidity";
import FileUploader from "Components/Admin/FileUploader";
import Checkbox from "Components/Admin/Form/Checkbox";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import Section from "Components/Admin/Section";
import { TextEditor } from "Components/Admin/TextEditor";

import * as S from "./styles";

const parentalRatingOptions = [
  { label: "Livre", value: "Livre" },
  { label: "10 anos", value: "10" },
  { label: "12 anos", value: "12" },
  { label: "14 anos", value: "14" },
  { label: "16 anos", value: "16" },
  { label: "18 anos", value: "18" },
];

const CreateProductions = () => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();
  const { uploadFile } = UseFile();

  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const [coverFile, setCoverFile] = useState(null);
  const [files, setFiles] = useState([]);

  const [isActive, setIsActive] = useState(false);

  const [parentalRating, setParentalRating] = useState(null);
  const [accessibility, setAccessibility] = useState([]);

  const formSchema = z.object(ProductionValidator);

  const methods = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      start_at: null,
      finish_at: null,
    },
  });
  const { register, reset } = methods;

  const submit = async (data) => {
    setLoading(true);

    try {
      if (!coverFile || !coverFile.length) {
        setLoading(false);
        return showNotification(
          "danger",
          "Erro!",
          "Selecione pelo menos uma imagem!",
        );
      }

      const images = [];

      const image_location = await uploadFile(coverFile[0]);
      images.push(image_location);

      for (const file of files) {
        const image_location = await uploadFile(file);
        images.push(image_location);
      }

      data.images = images;

      data.is_active = isActive;

      if (!isActive) {
        data.active_start_at = null;
        data.active_finish_at = null;
      }

      data.description = transformEditorStateIntoData(editorState);

      data.parental_rating = parentalRating?.value;
      data.accessibility = accessibility;

      if (!data?.description || data?.description === "<p><br></p>") {
        setLoading(false);
        return showNotification(
          "danger",
          "Erro!",
          "A descrição é obrigatória!",
        );
      }

      await api.post("/productions/create-productions", data);

      setLoading(false);

      showNotification("success", "Sucesso!", "Produção criada com sucesso!");

      reset();
      setFiles(null);
      setCoverFile(null);
      setEditorState(EditorState.createEmpty());

      setParentalRating(null);
      setAccessibility([]);
    } catch (error) {
      setLoading(false);
      return HTTPErrorCallback(error);
    }
  };

  const accessibilityOptions = [
    { img: Braile, value: "Braile" },
    { img: Cadeirante, value: "Cadeirante" },
    { img: Caption, value: "Caption" },
    { img: Libras, value: "Libras" },
    { img: Surdo, value: "Surdo" },
    { img: Text, value: "Text" },
  ];

  return (
    <Section>
      <S.Wrapper>
        <FormWrapper {...methods} submit={submit}>
          <Input
            {...register("title")}
            type="text"
            label="Título"
            placeholder="Título"
            required
          />
          <TextEditor
            name="description"
            editorState={editorState}
            setEditorState={setEditorState}
            key="description"
            label="Descrição"
            required
          />

          <DateValidity register={register} startRequired />

          <Input
            {...register("idealization")}
            type="text"
            label="Idealização"
            placeholder="Idealização"
          />

          <Input
            {...register("original")}
            type="text"
            label="Obra Original"
            placeholder="Obra Original"
          />

          <Input
            {...register("argument")}
            type="text"
            label="Argumento"
            placeholder="Argumento"
          />

          <Input
            {...register("text")}
            type="text"
            label="Texto"
            placeholder="Texto"
          />

          <Input
            {...register("dramaturgy")}
            type="text"
            label="Dramaturgia"
            placeholder="Dramaturgia"
          />

          <Input
            {...register("enactment")}
            type="text"
            label="Encenação"
            placeholder="Encenação"
          />

          <Input
            {...register("adaptation")}
            type="text"
            label="Adaptação"
            placeholder="Adaptação"
          />

          <Input
            {...register("direction")}
            type="text"
            label="Direção"
            placeholder="Direção"
          />

          <Input
            {...register("artistic_collaboration")}
            type="text"
            label="Colaboração Artística"
            placeholder="Colaboração Artística"
          />

          <Input
            {...register("musical_direction")}
            type="text"
            label="Direção Musical"
            placeholder="Direção Musical"
          />

          <Input
            {...register("movement_direction")}
            type="text"
            label="Direção de Movimento"
            placeholder="Direção de Movimento"
          />

          <Input
            {...register("coreography")}
            type="text"
            label="Coreografia"
            placeholder="Coreografia"
          />

          <Input
            {...register("audiovisual_direction")}
            type="text"
            label="Direção de Audiovisual"
            placeholder="Direção de Audiovisual"
          />

          <Input
            {...register("art_direction")}
            type="text"
            label="Direção de Arte"
            placeholder="Direção de Arte"
          />

          <Input
            {...register("cast")}
            type="text"
            label="Elenco"
            placeholder="Elenco"
          />

          <Input
            {...register("music")}
            type="text"
            label="Músicos"
            placeholder="Músicos"
          />

          <Input
            {...register("cenary")}
            type="text"
            label="Cenografia"
            placeholder="Cenografia"
          />

          <Input
            {...register("costumes")}
            type="text"
            label="Figurino"
            placeholder="Figurino"
          />

          <Input
            {...register("lightining")}
            type="text"
            label="Iluminação"
            placeholder="Iluminação"
          />

          <Input
            {...register("light_designer")}
            type="text"
            label="Designer de Luz"
            placeholder="Designer de Luz"
          />

          <Input
            {...register("sound_designer")}
            type="text"
            label="Designer de Som"
            placeholder="Designer de Som"
          />

          <Input
            {...register("research")}
            type="text"
            label="Pesquisa"
            placeholder="Pesquisa"
          />

          <Input
            {...register("cast_production")}
            type="text"
            label="Produção de Elenco"
            placeholder="Produção de Elenco"
          />

          <Input
            {...register("production_direction")}
            type="text"
            label="Direção de Produção"
            placeholder="Direção de Produção"
          />

          <Input
            {...register("associate_producer")}
            type="text"
            label="Produtor Associado"
            placeholder="Produtor Associado"
          />

          <Input
            {...register("production_coordination")}
            type="text"
            label="Coordenação de Produção"
            placeholder="Coordenação de Produção"
          />

          <Input
            {...register("realization")}
            type="text"
            label="Realização"
            placeholder="Realização"
          />

          <Input
            {...register("ticket_link")}
            type="text"
            label="Link do Ingresso"
            placeholder="Link do Ingresso"
          />

          <Input
            {...register("premiere_location")}
            type="text"
            label="Local de Estreia"
            placeholder="Local de Estreia"
          />

          <FileUploader
            label="Imagem de Capa"
            loading={loading}
            allowedExtensions={imageExtensions}
            setFiles={setCoverFile}
            files={coverFile}
            required
          />

          <FileUploader
            label="Carrossel de Imagens"
            loading={loading}
            allowedExtensions={imageExtensions}
            setFiles={setFiles}
            files={files}
            allowMultiple
          />

          <Checkbox
            label="Está em cartaz?"
            name="is_active"
            onChange={(e) => setIsActive(e.target.checked)}
          />

          {isActive && (
            <>
              <DateValidity
                register={register}
                required
                startName="active_start_at"
                finishName="active_finish_at"
                startLabel="Início de Exibição em Cartaz"
                finishLabel="Término de Exibição em Cartaz"
              />

              <Input
                {...register("vimeo_link")}
                type="text"
                label="Link do Vimeo"
                placeholder="Link do Vimeo"
              />

              <Input
                {...register("location")}
                type="text"
                label="Local"
                placeholder="Local"
              />

              <Input
                {...register("state")}
                type="text"
                label="Estado"
                placeholder="Estado"
              />

              <Input
                {...register("city")}
                type="text"
                label="Cidade"
                placeholder="Cidade"
              />

              {/* <DateValidity
                register={register}
                required
                startName="date_start_at"
                finishName="date_finish_at"
                startLabel="Início da Temporada"
                finishLabel="Término da Temporada"
              /> */}

              <Input
                {...register("season_dates")}
                type="text"
                label="Início e Término da Temporada"
                placeholder="Início e Término da Temporada"
              />

              <Input
                {...register("time")}
                type="text"
                label="Horários"
                placeholder="Horários"
              />

              <Select
                name="parental_rating"
                label="Classificação Indicativa"
                placeholder="Selecione uma Classificação"
                options={parentalRatingOptions}
                onChange={(e) => {
                  setParentalRating(e);
                }}
                value={parentalRating}
                required
              />

              <S.Accessibility>
                <label>Acessibilidade</label>

                <S.IconWrapper>
                  {accessibilityOptions.map((acc) => (
                    <S.Icon
                      src={acc.img}
                      alt={acc.value}
                      key={acc.value}
                      selected={accessibility.includes(acc.value)}
                      onClick={() => {
                        if (accessibility.includes(acc.value)) {
                          return setAccessibility(
                            accessibility.filter((a) => a !== acc.value),
                          );
                        }

                        const newAccessibility = [...accessibility, acc.value];
                        setAccessibility([...new Set(newAccessibility)]);
                      }}
                    />
                  ))}
                </S.IconWrapper>
              </S.Accessibility>
            </>
          )}

          <Button type="submit" text="Criar" disabled={loading} />
        </FormWrapper>
      </S.Wrapper>
    </Section>
  );
};

export default CreateProductions;
